import { Button } from '@mui/material';
import { useMutation, UseMutationOptions, useQueryClient } from '@tanstack/react-query';
import { updateStudy } from 'api/study';
import { SnackbarCloseButton } from 'components/Snackbars/SnackbarCloseButton';
import { Study, StudyUpdate } from 'interfaces/study';
import { compact, find, findIndex, first, keys, pick, slice } from 'es-toolkit/compat';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import React, { useCallback, useRef } from 'react';
import { useCurrentLabId } from './useCurrentLab';

type ContentEditableState = {
  previousValue: Study;
  updatedValue: Study;
  undoValue: Partial<StudyUpdate>;
};

type StudyUpdateField = Exclude<keyof StudyUpdate, 'id'>;

const useStudyMutations = (
  study?: Study,
  {
    onSuccess: additionalOnSuccess,
    onCloseSnackbar = () => {},
  }: {
    onSuccess?: (isUndo: boolean, updatedStudy: Partial<Study>, context: any) => void;
    onCloseSnackbar?: () => void;
  } = {}
) => {
  const studyId = study?.id;
  const { labId } = useCurrentLabId();
  const queryClient = useQueryClient();
  const STUDIES_WITH_STATISTICS_QUERY_KEY = ['studies', labId, 'withStatistics'];
  const STUDIES_QUERY_KEY = ['studies', labId];
  const isReady = Boolean(study && studyId);
  const inFlightFieldsRef = useRef<Partial<Record<StudyUpdateField, boolean>>>({});
  const queuedFieldValuesRef = useRef<Partial<Record<StudyUpdateField, StudyUpdate[StudyUpdateField]>>>({});

  const enqueueSnackbarSave = (studyState: ContentEditableState, undoChanges: boolean) => {
    closeSnackbar(getSnackbarKey(studyState.previousValue));

    enqueueSnackbar(undoChanges ? 'Changes discarded' : 'Changes saved', {
      key: getSnackbarKey(studyState.updatedValue),
      variant: 'default',
      style: { textTransform: 'none' },
      action: (key) => (
        <>
          {!undoChanges && (
            <Button
              data-cy={
                'undo-button-' +
                studyState.previousValue.id +
                studyState.previousValue.name +
                studyState.previousValue.description
              }
              color="secondary"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                closeSnackbar(key);
                studyUndoMutation.mutate(studyState.undoValue);
              }}
            >
              UNDO
            </Button>
          )}
          <SnackbarCloseButton snackbarKey={key} onClick={onCloseSnackbar} />
        </>
      ),
    });
  };

  const getSnackbarKey = (studySnapshot: Study) => {
    return JSON.stringify(studySnapshot);
  };

  const replaceUpdatedStudy = (oldStudies: Study[], studyUpdates: Partial<StudyUpdate>) => {
    const index = findIndex(compact(oldStudies), (item) => item.id === studyUpdates?.id);

    const updatedStudy = {
      ...(oldStudies?.[index] || {}),
      ...studyUpdates,
    };

    return [...slice(oldStudies, 0, index), updatedStudy, ...slice(oldStudies, index + 1)];
  };

  const getSingleStudyKeys = (targetStudyId: string) => ({
    withoutStatistics: ['study', targetStudyId, labId],
    withStatistics: ['study', targetStudyId, labId, 'withStatistics'],
  });

  const getUpdatedFieldNames = (studyUpdate: Partial<StudyUpdate>) =>
    keys(studyUpdate).filter((fieldName): fieldName is StudyUpdateField => fieldName !== 'id');

  const hasQueuedNewerValue = (studyUpdate: Partial<StudyUpdate>) =>
    getUpdatedFieldNames(studyUpdate).some((fieldName) => queuedFieldValuesRef.current[fieldName] !== undefined);

  const getCurrentStudy = (targetStudyId: string) => {
    const studyFromList = find(
      queryClient.getQueryData<Study[]>(STUDIES_QUERY_KEY),
      (currStudy: Study) => currStudy.id === targetStudyId
    );

    if (studyFromList) {
      return studyFromList;
    }

    const { withoutStatistics, withStatistics } = getSingleStudyKeys(targetStudyId);
    return (
      queryClient.getQueryData<Study>(withoutStatistics) || queryClient.getQueryData<Study>(withStatistics) || study
    );
  };

  const applyStudyUpdateToCache = (studyUpdate: Partial<StudyUpdate>) => {
    queryClient.setQueryData(STUDIES_QUERY_KEY, (old: any) => replaceUpdatedStudy(old, studyUpdate));
    queryClient.setQueryData(STUDIES_WITH_STATISTICS_QUERY_KEY, (old: any) => replaceUpdatedStudy(old, studyUpdate));

    const { withoutStatistics, withStatistics } = getSingleStudyKeys(studyUpdate.id as string);

    queryClient.setQueryData(withoutStatistics, (previousStudyData: Study) =>
      first(replaceUpdatedStudy([previousStudyData], studyUpdate))
    );
    queryClient.setQueryData(withStatistics, (previousStudyData: Study) =>
      first(replaceUpdatedStudy([previousStudyData], studyUpdate))
    );
  };

  const onMutate: UseMutationOptions<any, any, Partial<StudyUpdate>, any>['onMutate'] = async (studyUpdate) => {
    await queryClient.cancelQueries(STUDIES_QUERY_KEY);
    await queryClient.cancelQueries(STUDIES_WITH_STATISTICS_QUERY_KEY);

    const previousStudy = getCurrentStudy(studyUpdate.id as string);

    applyStudyUpdateToCache(studyUpdate);

    return { updateForUndo: pick(previousStudy, keys(studyUpdate)) };
  };

  const onError: UseMutationOptions<any, any, Partial<StudyUpdate>, any>['onError'] = (err, studyUpdate, context) => {
    if (hasQueuedNewerValue(studyUpdate)) {
      return;
    }

    queryClient.setQueryData(STUDIES_QUERY_KEY, (old: any) => replaceUpdatedStudy(old, context?.updateForUndo));
    queryClient.setQueryData(STUDIES_WITH_STATISTICS_QUERY_KEY, (old: any) =>
      replaceUpdatedStudy(old, context?.updateForUndo)
    );

    const { withoutStatistics: singleStudyWithoutStatistics, withStatistics: singleStudyWithStatistics } =
      getSingleStudyKeys(studyUpdate.id as string);

    queryClient.setQueryData(singleStudyWithoutStatistics, (previousStudyData: Study) =>
      first(replaceUpdatedStudy([previousStudyData], context?.updateForUndo))
    );
    queryClient.setQueryData(singleStudyWithStatistics, (previousStudyData: Study) =>
      first(replaceUpdatedStudy([previousStudyData], context?.updateForUndo))
    );

    enqueueSnackbar('Error occurred, changes are not saved', {
      variant: 'error',
    });
  };

  const onSettled: UseMutationOptions<any, any, Partial<StudyUpdate>, any>['onSettled'] = (
    data,
    error,
    studyUpdate
  ) => {
    if (hasQueuedNewerValue(studyUpdate)) {
      return;
    }

    queryClient.invalidateQueries(STUDIES_QUERY_KEY);
    queryClient.invalidateQueries(STUDIES_WITH_STATISTICS_QUERY_KEY);
    const { withoutStatistics: singleStudyWithoutStatistics, withStatistics: singleStudyWithStatistics } =
      getSingleStudyKeys(studyUpdate.id as string);
    queryClient.invalidateQueries(singleStudyWithoutStatistics);
    queryClient.invalidateQueries(singleStudyWithStatistics);
  };

  const onSuccess = (isUndo: boolean, studyUpdate: StudyUpdate, context: any) => {
    if (!study) {
      additionalOnSuccess?.(isUndo, studyUpdate, context);
      return;
    }

    if (hasQueuedNewerValue(studyUpdate)) {
      additionalOnSuccess?.(isUndo, studyUpdate, context);
      return;
    }

    const fullUpdatedStudy = { ...study, ...studyUpdate };
    const state: ContentEditableState = {
      previousValue: { ...study, ...context?.updateForUndo },
      updatedValue: fullUpdatedStudy,
      undoValue: { id: study.id, ...context?.updateForUndo },
    };
    enqueueSnackbarSave(state, isUndo);
    additionalOnSuccess?.(isUndo, studyUpdate, context);
  };

  const studyMutation = useMutation(updateStudy, {
    onMutate,
    onError,
    onSuccess: (data, studyUpdate: StudyUpdate, context) => onSuccess(false, studyUpdate, context),
    onSettled,
  });

  const studyUndoMutation = useMutation(updateStudy, {
    onMutate,
    onError,
    onSuccess: (data, studyUpdate: StudyUpdate, context) => onSuccess(true, studyUpdate, context),
    onSettled,
  });

  const sendFieldUpdate = useCallback(
    async <T extends StudyUpdateField>(fieldName: T, value: StudyUpdate[T]) => {
      if (!studyId) {
        return;
      }

      inFlightFieldsRef.current[fieldName] = true;

      let nextValue: StudyUpdate[T] | undefined = value;

      while (nextValue !== undefined) {
        await studyMutation.mutateAsync({
          id: studyId,
          [fieldName]: nextValue,
        } as Partial<StudyUpdate>);

        const queuedValue = queuedFieldValuesRef.current[fieldName] as StudyUpdate[T] | undefined;

        if (queuedValue !== undefined && queuedValue !== nextValue) {
          delete queuedFieldValuesRef.current[fieldName];
          nextValue = queuedValue;
          continue;
        }

        delete queuedFieldValuesRef.current[fieldName];
        nextValue = undefined;
      }

      delete inFlightFieldsRef.current[fieldName];
    },
    [studyId, studyMutation]
  );

  const handleFieldSave = useCallback(
    <T extends StudyUpdateField>(fieldName: T, value: StudyUpdate[T]) => {
      if (!study || !studyId) {
        return;
      }

      const queuedValue = queuedFieldValuesRef.current[fieldName] as StudyUpdate[T] | undefined;
      const currentValue = queuedValue ?? (study[fieldName] as StudyUpdate[T]);

      if (value === currentValue) {
        return;
      }

      if (inFlightFieldsRef.current[fieldName]) {
        queuedFieldValuesRef.current[fieldName] = value;
        applyStudyUpdateToCache({
          id: studyId,
          [fieldName]: value,
        } as Partial<StudyUpdate>);
        return;
      }

      void sendFieldUpdate(fieldName, value);
    },
    [applyStudyUpdateToCache, sendFieldUpdate, study]
  );

  const handleFieldSaveAsync = useCallback(
    async <T extends StudyUpdateField>(fieldName: T, value: StudyUpdate[T]) => {
      if (!study || !studyId) {
        return;
      }

      const queuedValue = queuedFieldValuesRef.current[fieldName] as StudyUpdate[T] | undefined;
      const currentValue = queuedValue ?? (study[fieldName] as StudyUpdate[T]);

      if (value === currentValue) {
        return;
      }

      if (inFlightFieldsRef.current[fieldName]) {
        queuedFieldValuesRef.current[fieldName] = value;
        applyStudyUpdateToCache({
          id: studyId,
          [fieldName]: value,
        } as Partial<StudyUpdate>);
        return;
      }

      await sendFieldUpdate(fieldName, value);
    },
    [applyStudyUpdateToCache, sendFieldUpdate, study]
  );

  return {
    handleFieldSave,
    handleFieldSaveAsync,
    studyMutation,
    isSaving: studyMutation.isLoading,
    isReady,
  };
};

export default useStudyMutations;
